/**
 * src/api/client.ts
 * ------------------
 * Axios instance pre-configured for all Helix Admin API calls.
 *
 * Responsibilities:
 *   - Base URL from VITE_API_BASE_URL env var
 *   - Attach Authorization: Bearer <token> header from Zustand auth store
 *   - Refresh token on 401 (single in-flight refresh, queue subsequent requests)
 *   - Retry logic: 3 retries for 5xx errors with exponential back-off
 *   - Request/response logging in development
 *   - Standardised error shape via HelixApiError
 *
 * All API modules (admin.ts, knowledge.ts, etc.) import this instance.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios'
import { API_BASE_PATH } from '@shared/constants'

export class HelixApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'HelixApiError'
  }
}

// In-flight refresh state (prevents parallel refresh races)
let isRefreshing = false
let refreshQueue: Array<() => void> = []

function processQueue() {
  refreshQueue.forEach((resolve) => resolve())
  refreshQueue = []
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_PATH,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ── Response interceptor: handle 401 with token refresh ─────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Token refresh on 401 — use HttpOnly cookie (no localStorage needed)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Don't try to refresh the refresh endpoint itself (infinite loop guard)
      if (originalRequest.url?.includes('/auth/refresh')) {
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push(() => resolve(apiClient(originalRequest)))
        })
      }

      isRefreshing = true
      try {
        // POST /auth/refresh — the refresh_token HttpOnly cookie is sent automatically
        await apiClient.post('/auth/refresh')
        processQueue()
        return apiClient(originalRequest)
      } catch {
        window.location.href = '/login'
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    // Normalise to HelixApiError
    const status = error.response?.status ?? 0
    const data = error.response?.data as Record<string, unknown> | undefined
    throw new HelixApiError(
      status,
      String(data?.code ?? 'UNKNOWN_ERROR'),
      String(data?.message ?? error.message),
      data?.detail,
    )
  },
)

export default apiClient
